
export type AiService = 'gemini' | 'openai';

// 에러 코드별 차단 시간 (ms)
const BLOCK_DURATIONS: Record<string, number> = {
    '401': 30 * 60 * 1000,  // 30분 — 인증 오류
    '403': 30 * 60 * 1000,  // 30분 — 권한 오류
    '429': 2 * 60 * 1000,   // 2분  — Rate limit
    '500': 60 * 1000,        // 1분  — 서버 오류
};
const DEFAULT_BLOCK_MS = 60 * 1000;

const blockKey = (service: AiService) => `xleven_ai_block_${service}`;

/** 해당 서비스가 현재 차단 중인지 확인 */
export const isServiceBlocked = (service: AiService): boolean => {
    try {
        const raw = sessionStorage.getItem(blockKey(service));
        if (!raw) return false;
        const { until } = JSON.parse(raw) as { until: number };
        if (Date.now() < until) return true;
        sessionStorage.removeItem(blockKey(service)); // 만료된 차단 해제
        return false;
    } catch {
        return false;
    }
};

/** 서비스를 일정 시간 차단 */
export const blockService = (service: AiService, code: string) => {
    const duration = BLOCK_DURATIONS[code] ?? DEFAULT_BLOCK_MS;
    try {
        sessionStorage.setItem(blockKey(service), JSON.stringify({
            until: Date.now() + duration,
            code,
        }));
        console.warn(`[AI Circuit Breaker] ${service} blocked for ${duration / 1000}s (error ${code})`);
    } catch {}
};

/** 서비스 차단 해제 (성공 시 호출) */
export const clearServiceBlock = (service: AiService) => {
    try {
        sessionStorage.removeItem(blockKey(service));
    } catch {}
};

/** 에러 객체에서 HTTP 상태 코드를 추출 */
export const extractStatusCode = (error: unknown): string => {
    const msg = String((error as any)?.message ?? error ?? '');
    // 숫자 상태 코드 직접 매칭
    const numMatch = msg.match(/\b(401|403|429|500|502|503)\b/);
    if (numMatch) return numMatch[1];
    // Gemini SDK 에러 메시지 패턴 매칭
    if (/unauthorized|api.?key.?invalid|invalid.?api.?key/i.test(msg)) return '401';
    if (/permission.?denied|forbidden/i.test(msg)) return '403';
    if (/resource.?exhausted|rate.?limit|too.?many.?request/i.test(msg)) return '429';
    if (/internal.?server|server.?error/i.test(msg)) return '500';
    return 'unknown';
};

const BLOCKED_CODES = new Set(['401', '403', '429', '500']);

/**
 * Gemini API 호출 래퍼.
 * 차단 상태이면 즉시 GEMINI_BLOCKED 에러를 던지고,
 * 401/403/429/500 응답 시 서비스를 차단한다.
 */
export const callGemini = async <T>(fn: () => Promise<T>): Promise<T> => {
    if (isServiceBlocked('gemini')) throw new Error('GEMINI_BLOCKED');
    try {
        const result = await fn();
        clearServiceBlock('gemini');
        return result;
    } catch (error) {
        const code = extractStatusCode(error);
        if (BLOCKED_CODES.has(code)) blockService('gemini', code);
        throw error;
    }
};

/**
 * OpenAI API 호출 래퍼.
 * 차단 상태이면 즉시 OPENAI_BLOCKED 에러를 던지고,
 * 401/403/429/500 응답 시 서비스를 차단한다.
 */
export const callOpenAI = async <T>(fn: () => Promise<T>): Promise<T> => {
    if (isServiceBlocked('openai')) throw new Error('OPENAI_BLOCKED');
    try {
        const result = await fn();
        clearServiceBlock('openai');
        return result;
    } catch (error) {
        const code = extractStatusCode(error);
        if (BLOCKED_CODES.has(code)) blockService('openai', code);
        throw error;
    }
};
